import type { Distance, Point } from "@pdfking/math"
import { GetXY, Max } from "@pdfking/math"
import { SortBy } from "@pdfking/tfunc"

import type { Drawable, ImpositionNode } from "#internal"

import { CUT_COLOR_ID } from "../../../data/ColorList"
import { CreatePath } from "../../../shapes/CreatePath"
import { DistanceRound } from "../../../utils/DistanceRound"
import { GrowGuides } from "./GrowGuides"

/** Every outermost grow under `node`, `node` itself when it is one. */
function* Grows(node: ImpositionNode): Generator<ImpositionNode> {
  if (node.data?.grow) {
    yield node
    return
  }
  for (const child of node.children) yield* Grows(child)
}

/** One line of the grid: across x at `at` (a horizontal line) or across y. */
type Line = { along: keyof Point<unknown>; at: Distance; spans: [Distance, Distance][] }

/**
 * The outline of every grow on the sheet where it landed, as one grid of
 * lines: where edges of neighbouring grows lie on one line and touch or
 * overlap they are one cut, run once. Hung once on the sheet.
 */
export const DrawCutLines: Drawable = function* (node) {
  const lines = new Map<string, Line>()
  const add = (along: Line["along"], at: Distance, from: Distance, to: Distance) => {
    const key = `${along} ${DistanceRound(at)}`
    const line = lines.get(key) ?? { along, at, spans: [] }
    line.spans.push([from, to])
    lines.set(key, line)
  }
  for (const grow of Grows(node)) {
    const data = grow.data?.grow
    if (!data?.grow.show_cut) continue
    const landed = GrowGuides(grow, data)
    if (!landed) continue
    const { x1, x2, y1, y2 } = GetXY(landed.canvas)
    add("x", y1, x1, x2)
    add("x", y2, x1, x2)
    add("y", x1, y1, y2)
    add("y", x2, y1, y2)
  }
  for (const { along, at, spans } of lines.values()) {
    const merged: [Distance, Distance][] = []
    for (const [from, to] of SortBy(spans, ([from]) => DistanceRound(from))) {
      const last = merged[merged.length - 1]
      if (last && DistanceRound(from) <= DistanceRound(last[1])) last[1] = Max(last[1], to)
      else merged.push([from, to])
    }
    for (const [from, to] of merged) {
      yield CreatePath(
        along === "x"
          ? [
              { x: from, y: at },
              { x: to, y: at },
            ]
          : [
              { x: at, y: from },
              { x: at, y: to },
            ],
        CUT_COLOR_ID
      )
    }
  }
}
